import { 
  WellbeingReportsResponse, 
  WellbeingFact, 
  ProcessedWellbeingData,
  WellbeingMetricSummary,
  DailyWellbeingData,
  WellbeingTrends,
  WellbeingWeekData
} from '../types/reports';
import { format, parseISO, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';

/**
 * Centralized wellbeing metrics configuration
 * Single source of truth for all wellbeing metric metadata
 */
export interface WellbeingMetricConfig {
  key: string;
  label: string;
  question: string;
  lowLabel: string;
  highLabel: string;
  invertedScale?: boolean;
  color: string;
}

/**
 * Complete wellbeing metrics configuration - matches Check-in modal order
 */
export const WELLBEING_METRICS: readonly WellbeingMetricConfig[] = [
  {
    key: 'wellbeing_mood_state',
    label: 'Overall Wellbeing',
    question: 'How are you feeling today?',
    lowLabel: 'Poor',
    highLabel: 'Excellent',
    color: '#ef4444', // Red
    invertedScale: false
  },
  {
    key: 'wellbeing_stress_level',
    label: 'Stress Level',
    question: 'How stressed are you feeling?',
    lowLabel: 'Very calm',
    highLabel: 'Very stressed',
    color: '#f59e0b', // Orange
    invertedScale: true
  },
  {
    key: 'wellbeing_work_life_balance',
    label: 'Work-Life Balance',
    question: "How's your work-life balance?",
    lowLabel: 'Unbalanced',
    highLabel: 'Well balanced',
    color: '#84cc16', // Lime
    invertedScale: false
  },
  {
    key: 'wellbeing_focus',
    label: 'Focus Ability',
    question: 'How well can you focus today?',
    lowLabel: 'Distracted',
    highLabel: 'Laser focused',
    color: '#3b82f6', // Blue
    invertedScale: false
  },
  {
    key: 'wellbeing_energy',
    label: 'Energy Level',
    question: "What's your energy level like?",
    lowLabel: 'Exhausted',
    highLabel: 'Energized',
    color: '#06b6d4', // Cyan
    invertedScale: false
  },
  {
    key: 'wellbeing_social_support',
    label: 'Social Support',
    question: 'How supported do you feel socially?',
    lowLabel: 'Isolated',
    highLabel: 'Well connected',
    color: '#10b981', // Green
    invertedScale: false
  },
  {
    key: 'wellbeing_sleep',
    label: 'Sleep Quality',
    question: 'How was your sleep recently?',
    lowLabel: 'Poor sleep',
    highLabel: 'Great sleep',
    color: '#8b5cf6', // Purple
    invertedScale: false
  }
] as const;

/**
 * Get metric configuration by key
 */
export function getMetricConfig(key: string): WellbeingMetricConfig | undefined {
  return WELLBEING_METRICS.find(m => m.key === key);
}

/**
 * Get all metric keys in order
 */
export function getMetricKeys(): string[] {
  return WELLBEING_METRICS.map(m => m.key);
}

/**
 * Get all metric labels in order
 */
export function getMetricLabels(): string[] {
  return WELLBEING_METRICS.map(m => m.label);
}

/**
 * Process raw wellbeing facts into organized data structures for components
 */
export function processWellbeingData(
  response: WellbeingReportsResponse, 
  requestedDateRange?: { startDay: string; endDay: string }
): ProcessedWellbeingData {
  const { facts, summary } = response;
  
  // Group facts by metric key
  const factsByKey = groupFactsByKey(facts);
  
  // Create overall metric summaries
  const overall = createMetricSummaries(factsByKey);
  
  // Use requested date range if provided, otherwise fall back to response date range
  const dateRangeToUse = requestedDateRange 
    ? { startDate: requestedDateRange.startDay, endDate: requestedDateRange.endDay }
    : summary.dateRange;
  
  // Create daily aggregations using the correct date range
  const daily = createDailyAggregations(facts, dateRangeToUse);
  
  // Calculate trends
  const trends = calculateTrends(factsByKey);
  
  return {
    summary,
    overall,
    daily,
    trends
  };
}

/**
 * Group facts by metric key for easier processing
 */
function groupFactsByKey(facts: WellbeingFact[]): Record<string, WellbeingFact[]> {
  return facts.reduce((acc, fact) => {
    if (!acc[fact.key]) {
      acc[fact.key] = [];
    }
    acc[fact.key].push(fact);
    return acc;
  }, {} as Record<string, WellbeingFact[]>);
}

/**
 * Create metric summaries for each wellbeing dimension
 */
function createMetricSummaries(factsByKey: Record<string, WellbeingFact[]>): WellbeingMetricSummary[] {
  const keyOrder = getMetricKeys();
  
  const summaries = Object.entries(factsByKey).map(([key, facts]) => {
    const values = facts.map(f => f.value);
    const total = values.reduce((sum, val) => sum + val, 0);
    const count = values.length;
    const average = count > 0 ? total / count : 0;
    
    // Sort by date to get latest and calculate trend
    const sortedFacts = [...facts].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    
    const latest = sortedFacts.length > 0 ? sortedFacts[sortedFacts.length - 1].value : null;
    const first = sortedFacts.length > 0 ? sortedFacts[0].value : null;
    
    let trend = null;
    if (first !== null && latest !== null && first !== 0) {
      trend = ((latest - first) / first) * 100;
    }
    
    return {
      key,
      label: formatKeyLabel(key),
      values,
      total,
      count,
      average,
      latest,
      trend
    };
  });
  
  // Sort summaries based on the defined order
  return summaries.sort((a, b) => {
    const indexA = keyOrder.indexOf(a.key);
    const indexB = keyOrder.indexOf(b.key);
    
    // If both keys are in the order list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    // If only A is in the list, A comes first
    if (indexA !== -1) return -1;
    
    // If only B is in the list, B comes first
    if (indexB !== -1) return 1;
    
    // If neither is in the list, sort alphabetically by label
    return a.label.localeCompare(b.label);
  });
}

/**
 * Create daily aggregations of wellbeing data
 */
function createDailyAggregations(
  facts: WellbeingFact[], 
  dateRange: { startDate: string; endDate: string }
): DailyWellbeingData[] {
  const startDate = parseISO(dateRange.startDate);
  const endDate = parseISO(dateRange.endDate);
  
  // Get all days in the range
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  return allDays.map(day => {
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);
    const dateStr = format(day, 'yyyy-MM-dd');
    
    // Get facts for this day
    const dayFacts = facts.filter(fact => {
      const factDate = parseISO(fact.createdAt);
      return factDate >= dayStart && factDate <= dayEnd;
    });
    
    // Group by metric key
    const metricsByKey = groupFactsByKey(dayFacts);
    
    // Calculate metrics for each key
    const metrics: { [key: string]: { value: number; count: number; average: number } } = {};
    
    Object.entries(metricsByKey).forEach(([key, keyFacts]) => {
      const values = keyFacts.map(f => f.value);
      const total = values.reduce((sum, val) => sum + val, 0);
      const count = values.length;
      const average = count > 0 ? total / count : 0;
      
      metrics[key] = {
        value: total,
        count,
        average
      };
    });
    
    return {
      date: dateStr,
      metrics,
      totalEntries: dayFacts.length
    };
  });
}

/**
 * Calculate trends for each metric
 */
function calculateTrends(factsByKey: Record<string, WellbeingFact[]>): WellbeingTrends {
  const trends: WellbeingTrends = {};
  
  Object.entries(factsByKey).forEach(([key, facts]) => {
    if (facts.length === 0) {
      trends[key] = {
        current: null,
        previous: null,
        change: null,
        isPositive: false
      };
      return;
    }
    
    // Sort by date
    const sortedFacts = [...facts].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    
    // Get most recent value
    const current = sortedFacts[sortedFacts.length - 1]?.value || null;
    
    // Calculate average of first half vs second half for trend
    const midpoint = Math.floor(sortedFacts.length / 2);
    const firstHalf = sortedFacts.slice(0, midpoint);
    const secondHalf = sortedFacts.slice(midpoint);
    
    let previous = null;
    let change = null;
    
    if (firstHalf.length > 0 && secondHalf.length > 0) {
      const firstAvg = firstHalf.reduce((sum, f) => sum + f.value, 0) / firstHalf.length;
      const secondAvg = secondHalf.reduce((sum, f) => sum + f.value, 0) / secondHalf.length;
      
      previous = firstAvg;
      change = secondAvg - firstAvg;
    }
    
    trends[key] = {
      current,
      previous,
      change,
      isPositive: change !== null ? change >= 0 : false
    };
  });
  
  return trends;
}

/**
 * Convert processed data back to legacy week format for charts
 * This creates synthetic "weeks" from the date range for backward compatibility
 */
export function convertToLegacyWeekFormat(processed: ProcessedWellbeingData): WellbeingWeekData[] {
  const { daily, summary } = processed;
  
  if (daily.length === 0) return [];
  
  // For simplicity, create one "week" representing the entire date range
  // This maintains chart compatibility while using the new data
  const weekData: WellbeingWeekData = {
    weekStartDate: summary.dateRange.startDate,
    weekEndDate: summary.dateRange.endDate,
    keys: processed.overall.map(metric => ({
      key: metric.key,
      values: metric.values,
      total: metric.total,
      count: metric.count,
      average: metric.average
    }))
  };
  
  return [weekData];
}

/**
 * Format wellbeing key for display
 */
export function formatKeyLabel(key: string): string {
  const config = getMetricConfig(key);
  if (config) {
    return config.label;
  }
  
  // Fallback for any unmapped keys
  return key
    .replace('wellbeing_', '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get metric scale labels for consistency across components
 */
export function getMetricScale(key: string): { lowLabel: string; highLabel: string; invertedScale?: boolean } {
  const config = getMetricConfig(key);
  if (config) {
    return {
      lowLabel: config.lowLabel,
      highLabel: config.highLabel,
      invertedScale: config.invertedScale
    };
  }
  
  return { lowLabel: 'Low', highLabel: 'High' };
}

/**
 * Get metric color for consistency across components
 */
export function getMetricColor(key: string): string {
  const config = getMetricConfig(key);
  return config?.color || '#64748b'; // Default gray
}

/**
 * Calculate percentage change between two values
 */
export function calculatePercentageChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}